import { MdOutlineArrowDropDown, MdOutlineArrowDropUp } from 'react-icons/md';
import classNames from 'classnames';
import { HTMLAttributes, memo, useMemo } from 'react';
import {
  useTable,
  useSortBy,
  usePagination,
  useGlobalFilter,
  useAsyncDebounce,
} from 'react-table';
import LogoBlinkerCenter from '../../../components/app/LogoBlinkerCenter';
import { useWidth } from '../../../hooks/layout';

interface ITableChartProps {
  columns: string[];
  data: Record<string, string | number>[];
  standalone?: boolean;
  loading?: boolean;
}

function TableChart({
  columns,
  data,
  className,
  loading,
}: HTMLAttributes<HTMLTableElement> & ITableChartProps) {
  const [ref, width] = useWidth<HTMLDivElement>();

  const memoData = useMemo(() => data, [data]);
  const memoColumns = useMemo(
    () => columns.map((c) => ({ accessor: c, Header: c })),
    [columns]
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize },
    setGlobalFilter,
  } = useTable(
    {
      columns: memoColumns,
      data: memoData,
      initialState: { pageIndex: 0, pageSize: 10 },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );
  const onSearchInputChange = useAsyncDebounce((value) => {
    setGlobalFilter(value || undefined);
  }, 200);

  if (loading) {
    return <LogoBlinkerCenter />;
  }

  return (
    <div
      ref={ref}
      className={classNames('flex flex-col w-full h-full pb-1', className)}
    >
      <div
        className={classNames('flex-1 table-dense overflow-x-scroll xscroll')}
      >
        <table
          className={classNames(
            'border table table-compact w-full table-zebra border-collapse'
          )}
          style={{
            borderSpacing: '0',
          }}
          {...getTableProps()}
        >
          <thead className='w-full'>
            {headerGroups.map((headerGroup) => {
              return (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column) => {
                    return (
                      <th
                        {...column.getHeaderProps(
                          column.getSortByToggleProps()
                        )}
                        style={{
                          borderRadius: 0,
                          zIndex: 12,
                          top: 0,
                          position: 'sticky',
                          left: 'unset',
                          padding: '0.7rem',
                        }}
                        className='cursor-pointer sticky border-b-1 border-r border-t'
                      >
                        <div className='flex items-center'>
                          <div className='font-bold'>
                            {column.render('Header')}
                          </div>
                          <div className='pl-3 w-10'>
                            {
                              // eslint-disable-next-line
                              column.isSorted &&
                                (column.isSortedDesc ? (
                                  <MdOutlineArrowDropDown size='0.9rem' />
                                ) : (
                                  <MdOutlineArrowDropUp size='0.9rem' />
                                ))
                            }
                          </div>
                        </div>
                      </th>
                    );
                  })}
                </tr>
              );
            })}
          </thead>
          <tbody {...getTableBodyProps()}>
            {page.map((row) => {
              prepareRow(row);
              return (
                <tr {...row.getRowProps()}>
                  {row.cells.map((cell) => {
                    return (
                      <td
                        {...cell.getCellProps()}
                        style={{
                          padding: '0.7rem',
                        }}
                        className='border-b-content border-r'
                      >
                        {cell.render('Cell')}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className='h-3' />
      <div className='flex pagination items-center'>
        <div className='flex-1 flex items-center'>
          {width && width > 750 && (
            <div className='btn-group'>
              <button
                className={classNames('btn btn-sm btn-primary', {
                  'btn-disabled': !canPreviousPage,
                })}
                onClick={() => gotoPage(0)}
              >
                «
              </button>
              <button
                className={classNames('btn btn-sm btn-primary', {
                  'btn-disabled': !canPreviousPage,
                })}
                onClick={() => previousPage()}
              >
                ‹
              </button>
              <button
                className={classNames('btn btn-sm btn-primary', {
                  'btn-disabled': !canNextPage,
                })}
                onClick={() => nextPage()}
              >
                ›
              </button>
              <button
                className={classNames('btn btn-sm btn-primary', {
                  'btn-disabled': !canNextPage,
                })}
                onClick={() => gotoPage(pageCount - 1)}
              >
                »
              </button>
            </div>
          )}
          <div className='w-3' />
          <span>
            {' Page '}
            <strong>
              {pageIndex + 1} of {pageOptions.length}
            </strong>
          </span>
          <div className='w-3' />
          <input
            type='text'
            placeholder='search in results'
            className='input input-bordered input-sm w-36'
            onChange={(e) => {
              onSearchInputChange(e.target.value);
            }}
          />
          <div className='w-3' />
        </div>
        <div className='flex'>
          <span>
            {'Go to '}
            <input
              type='number'
              className='input input-sm input-primary w-20 text-center'
              defaultValue={pageIndex + 1}
              onChange={(e) => {
                const nPage = e.target.value ? Number(e.target.value) - 1 : 0;
                gotoPage(nPage);
              }}
            />
          </span>
          <div className='w-3' />
          <select
            className='select select-sm max-w-xs'
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              e.stopPropagation();
              e.preventDefault();
              e.target.blur();
            }}
          >
            {[10, 20, 30, 40, 50].map((nPageSize) => (
              <option key={nPageSize} value={nPageSize}>
                Show {nPageSize}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}

export default memo(TableChart);
